import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { hapticGraceExpiry } from '../../utils/haptics';
const PitchTracker = ({
  notes = [],            // full array of { display, frequency, duration }
  currentNoteIndex = 0,
  currentFrequency,
  confidence = 1.0,
  noteState = 'active',
  isActive,
  graceMs = 1000,
  onProgress,
  height = 320,          // CSS height of the canvas wrapper — pass '100%' for flex-fill
}) => {
  const canvasRef          = useRef(null);
  const animationRef        = useRef(null);
  const startTimeRef        = useRef(null);
  const graceStartRef       = useRef(null);
  // Approach phase: 2.5 s before grace where the first note flies in from the right
  const approachStartRef    = useRef(null);

  // Cumulative track position across completed notes
  const completedDurationRef  = useRef(0);
  const prevNoteIndexRef      = useRef(currentNoteIndex);
  // Tracks whether we've fired the grace-expiry haptic for the current note
  const graceHapticFiredRef   = useRef(false);

  // Prop mirrors — updated synchronously so the RAF always reads fresh values
  const currentFrequencyRef = useRef(currentFrequency);
  const confidenceRef       = useRef(confidence);
  const noteStateRef        = useRef(noteState);
  const currentNoteIndexRef = useRef(currentNoteIndex);
  const notesRef            = useRef(notes);

  useEffect(() => { currentFrequencyRef.current = currentFrequency; }, [currentFrequency]);
  useEffect(() => {
    confidenceRef.current = confidence;
    noteStateRef.current  = noteState;
  }, [confidence, noteState]);
  useEffect(() => {
    currentNoteIndexRef.current = currentNoteIndex;
    notesRef.current            = notes;
  }, [currentNoteIndex, notes]);

  // ─── Main animation effect ────────────────────────────────────────────────
  // Re-runs when recording starts/stops OR the current note advances.
  // On each note advance: accumulate the finished note's duration and restart
  // the per-note grace/timer without touching the overall visual position.
  useEffect(() => {
    // Accumulate duration of the note that just finished (if note changed)
    const prevIdx = prevNoteIndexRef.current;
    if (prevIdx !== currentNoteIndex) {
      const prevNote = notesRef.current[prevIdx];
      if (prevNote) completedDurationRef.current += prevNote.duration ?? 0;
      prevNoteIndexRef.current = currentNoteIndex;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx  = canvas.getContext('2d');

    startTimeRef.current        = null;
    graceStartRef.current       = null;
    graceHapticFiredRef.current = false;
    // Approach phase runs only at lesson start (note 0, no prior duration)
    if (currentNoteIndex === 0) approachStartRef.current = null;

    // ballY persists across frames for smooth animation; starts at canvas centre
    let ballY = 160;

    // Anti-jitter colour zone state
    let currentColorZone = 'silent';
    let pendingColorZone = null;
    let pendingZoneStart = 0;
    const COLOR_DWELL_MS = 150;

    let running = true;

    const draw = (timestamp) => {
      if (!running) return;

      // ── Dynamic sizing: resize canvas each frame to match container ──────
      // This keeps the canvas pixel-sharp after device rotation without
      // waiting for a note boundary to trigger a useEffect re-run.
      const dpr      = window.devicePixelRatio || 1;
      const cssW     = canvas.parentElement?.clientWidth  || 300;
      const cssH     = canvas.parentElement?.clientHeight || 320;
      const CANVAS_H = cssH;
      const physW    = Math.round(cssW * dpr);
      const physH    = Math.round(cssH * dpr);
      if (canvas.width !== physW || canvas.height !== physH) {
        canvas.width  = physW;
        canvas.height = physH;
      }
      // setTransform resets the matrix each frame — no cumulative scale drift
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const W             = cssW;
      const PADDING       = 4;
      const BAR_H         = 84;
      const barY          = CANVAS_H / 2 - BAR_H / 2;
      const barW          = W - PADDING * 2;
      const BALL_R        = 20;
      const MAX_DEV       = BAR_H / 2 - BALL_R - 2;
      const WINDOW_SECS   = 4.5;
      const BALL_X_FRAC   = 0.26;
      const PX_PER_SEC    = barW / WINDOW_SECS;
      const BALL_SCREEN_X = PADDING + BALL_X_FRAC * barW;

      const ns           = notesRef.current;
      const noteIdx      = currentNoteIndexRef.current;
      const currentNote  = ns[noteIdx];
      if (!currentNote) return;

      const currentFreq = currentFrequencyRef.current;
      const targetFreq  = currentNote.frequency;

      // ── Approach phase (first note only, 2.5 s before grace) ─────────────
      const APPROACH_SECS = 2.5;
      const isFirstNote   = noteIdx === 0 && completedDurationRef.current === 0;
      if (isFirstNote && !approachStartRef.current) approachStartRef.current = timestamp;
      const approachElapsed = isFirstNote && approachStartRef.current
        ? (timestamp - approachStartRef.current) / 1000
        : APPROACH_SECS; // notes > 0 skip approach immediately
      const inApproach  = approachElapsed < APPROACH_SECS;
      const approachFrac = Math.min(approachElapsed / APPROACH_SECS, 1);

      // ── Per-note grace / timer (blocked until approach finishes) ─────────
      // Only the first note gets a grace countdown; subsequent notes start immediately
      // to keep rhythm tight and avoid artificial gaps between exercises.
      const effectiveGraceMs = noteIdx === 0 ? graceMs : 0;
      let graceElapsed = 0, inGrace = false, hasFirstPitch = false;
      let elapsed = 0, noteProgress = 0;
      if (!inApproach) {
        if (!graceStartRef.current) graceStartRef.current = timestamp;
        graceElapsed  = timestamp - graceStartRef.current;
        const graceExpired = graceElapsed >= effectiveGraceMs;
        hasFirstPitch = currentFreq > 0;
        if (!startTimeRef.current && (graceExpired || hasFirstPitch)) {
          startTimeRef.current = timestamp;
          if (!graceHapticFiredRef.current) {
            graceHapticFiredRef.current = true;
            hapticGraceExpiry();
          }
        }
        elapsed      = startTimeRef.current ? timestamp - startTimeRef.current : 0;
        noteProgress = Math.min(elapsed / (currentNote.duration * 1000), 1);
        inGrace      = !startTimeRef.current;
        if (onProgress) onProgress(noteProgress);
      }

      // Current lesson position: negative during approach (note flies in), normal after
      const currentTime = inApproach
        ? (approachElapsed - APPROACH_SECS)   // −2.5 → 0  (first boundary arrives at ball)
        : completedDurationRef.current + noteProgress * currentNote.duration;
      const timeToX = (t) => BALL_SCREEN_X + (t - currentTime) * PX_PER_SEC;

      // ── Approach rainbow glow colour (cycles once over 2.5 s) ────────────
      const glowHue  = (timestamp * 0.15) % 360;
      const glowL    = 55 + 10 * Math.sin(timestamp / 300);
      const glowColor = `hsl(${glowHue},100%,${glowL}%)`;

      // ── Deviation & colour zones ───────────────────────────────────────
      const hasConfidentPitch = confidenceRef.current >= 0.6;
      const isSilent = currentFreq <= 0 || !hasConfidentPitch;
      let deviation = 0, rawCents = 0;

      if (currentFreq > 0 && targetFreq > 0 && hasConfidentPitch) {
        rawCents  = Math.abs(1200 * Math.log2(currentFreq / targetFreq));
        deviation = rawCents <= 30 ? 0 : rawCents <= 120 ? (rawCents - 30) / 90 : 1;
      } else {
        deviation = 1;
      }

      const smoothAlpha = rawCents > 120 ? 0.25 : 0.07;
      ballY += (barY + BAR_H / 2 + deviation * MAX_DEV - ballY) * smoothAlpha;

      // During approach → use rainbow glow (handled separately in drawing)
      // During grace → amber "get ready" zone
      const rawZone = inApproach ? 'ready'
        : inGrace ? 'ready'
        : isSilent ? 'silent'
        : rawCents < 45 ? 'green'
        : rawCents < 80 ? 'yellow'
        : 'red';

      if (rawZone !== currentColorZone) {
        if (pendingColorZone !== rawZone) {
          pendingColorZone = rawZone;
          pendingZoneStart = timestamp;
        } else if (timestamp - pendingZoneStart >= COLOR_DWELL_MS) {
          currentColorZone = rawZone;
          pendingColorZone = null;
        }
      } else {
        pendingColorZone = null;
      }

      const ZONE_COLORS = {
        ready:  { bar: '#ff9800', ball: '#ffb74d' },  // amber — "get ready"
        silent: { bar: '#f44336', ball: '#ef5350' },
        green:  { bar: '#4caf50', ball: '#66bb6a' },
        yellow: { bar: '#ffc107', ball: '#ffca28' },
        red:    { bar: '#f44336', ball: '#ef5350' },
      };
      const { bar: barColor, ball: ballColor } =
        ZONE_COLORS[currentColorZone] || ZONE_COLORS.silent;

      // ── Clear ─────────────────────────────────────────────────────────
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, W, CANVAS_H);

      // ── Track (clipped) ───────────────────────────────────────────────
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(PADDING, barY, barW, BAR_H, 12);
      ctx.clip();

      ctx.fillStyle = '#272727';
      ctx.fillRect(PADDING, barY, barW, BAR_H);

      // Colour fill: during approach — rainbow charge-up; during grace — amber; during singing — pitch colour
      if (isActive) {
        if (inApproach) {
          // Rainbow glow grows from left to ball position as note approaches
          const fillW = approachFrac * (BALL_SCREEN_X - PADDING);
          const pulseA = 0.55 + 0.35 * Math.abs(Math.sin(timestamp / 280));
          ctx.globalAlpha = pulseA;
          ctx.fillStyle = glowColor;
          ctx.fillRect(PADDING, barY, fillW, BAR_H);
          ctx.globalAlpha = 1;
        } else if (inGrace) {
          const graceProgress = Math.min(graceElapsed / graceMs, 1);
          const chargeFillW = graceProgress * (BALL_SCREEN_X - PADDING);
          const pulseAlpha = 0.7 + 0.3 * Math.abs(Math.sin(timestamp / 300));
          ctx.globalAlpha = pulseAlpha;
          ctx.fillStyle = barColor;
          ctx.fillRect(PADDING, barY, chargeFillW, BAR_H);
          ctx.globalAlpha = 1;
        } else {
          ctx.fillStyle = barColor;
          ctx.fillRect(PADDING, barY, BALL_SCREEN_X - PADDING, BAR_H);
        }
      }

      // Alternating tint for upcoming note zones
      let accDur = 0;
      for (let i = 0; i < ns.length; i++) {
        const zStart = accDur;
        const zEnd   = accDur + (ns[i].duration ?? 0);
        accDur = zEnd;
        if (i <= noteIdx) continue;
        const x1 = timeToX(zStart);
        const x2 = timeToX(zEnd);
        if (x2 < PADDING || x1 > PADDING + barW) continue;
        ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)';
        ctx.fillRect(
          Math.max(x1, PADDING), barY,
          Math.min(x2, PADDING + barW) - Math.max(x1, PADDING), BAR_H,
        );
      }

      ctx.restore();

      // Centre guide line
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(PADDING, barY + BAR_H / 2);
      ctx.lineTo(PADDING + barW, barY + BAR_H / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // ── Note-boundary milestones (time-based, always visible in window) ──
      let accDur2 = 0;
      for (let i = 0; i < ns.length; i++) {
        accDur2 += ns[i].duration ?? 0;
        const bx = timeToX(accDur2);
        if (bx < PADDING - 40 || bx > PADDING + barW + 40) continue;

        const isNext      = (i === noteIdx);
        const isCompleted = (i < noteIdx);
        const nextNote    = ns[i + 1];
        const secsAway    = accDur2 - currentTime;

        ctx.save();
        ctx.strokeStyle = isNext
          ? 'rgba(255,215,0,0.9)'
          : isCompleted
          ? 'rgba(255,255,255,0.10)'
          : 'rgba(255,255,255,0.30)';
        ctx.lineWidth = isNext ? 2.5 : 1.5;
        ctx.setLineDash(isNext ? [5, 3] : [3, 6]);
        ctx.beginPath();
        ctx.moveTo(bx, barY - 5);
        ctx.lineTo(bx, barY + BAR_H + 5);
        ctx.stroke();
        ctx.setLineDash([]);

        // ── Last note: draw "END" pill instead of a next-note pill ──
        if (!nextNote && !isCompleted) {
          const label   = 'END';
          ctx.font         = isNext ? 'bold 13px Arial' : '12px Arial';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const lw    = ctx.measureText(label).width;
          const pillW = lw + 18;
          const pillH = 21;
          const pillX = Math.max(PADDING + 2, Math.min(bx - pillW / 2, PADDING + barW - pillW - 2));
          const pillY = barY - pillH - 8;
          let alpha = isNext ? 1.0 : 0.65;
          if (isNext && secsAway < 1.5) {
            alpha = 0.8 + 0.2 * Math.abs(Math.sin(timestamp / 250));
          }
          ctx.globalAlpha = alpha;
          ctx.fillStyle   = isNext ? 'rgba(60,0,0,0.95)' : 'rgba(15,18,30,0.85)';
          ctx.strokeStyle = isNext ? '#ff6060' : 'rgba(220,100,100,0.45)';
          ctx.lineWidth   = 1.2;
          ctx.beginPath();
          ctx.roundRect(pillX, pillY, pillW, pillH, 6);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle    = isNext ? '#ff8a80' : 'rgba(255,160,160,0.88)';
          ctx.fillText(label, pillX + pillW / 2, pillY + pillH / 2);
          ctx.globalAlpha  = 1;
          ctx.textBaseline = 'alphabetic';
        }

        if (nextNote && !isCompleted) {
          const label = isNext ? ('NEXT  ' + nextNote.display) : nextNote.display;
          ctx.font         = isNext ? 'bold 13px Arial' : '12px Arial';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const lw    = ctx.measureText(label).width;
          const pillW = lw + 18;
          const pillH = 21;
          const pillX = Math.max(PADDING + 2, Math.min(bx - pillW / 2, PADDING + barW - pillW - 2));
          const pillY = barY - pillH - 8;

          let alpha = isNext ? 1.0 : 0.65;
          if (isNext && secsAway < 1.5) {
            alpha = 0.8 + 0.2 * Math.abs(Math.sin(timestamp / 250));
          }

          ctx.globalAlpha = alpha;
          ctx.fillStyle   = isNext ? 'rgba(25,18,0,0.95)' : 'rgba(15,18,30,0.85)';
          ctx.strokeStyle = isNext ? '#FFD700' : 'rgba(160,175,220,0.45)';
          ctx.lineWidth   = 1.2;
          ctx.beginPath();
          ctx.roundRect(pillX, pillY, pillW, pillH, 6);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle    = isNext ? '#FFD700' : 'rgba(200,215,255,0.88)';
          ctx.fillText(label, pillX + pillW / 2, pillY + pillH / 2);
          ctx.globalAlpha  = 1;
          ctx.textBaseline = 'alphabetic';
        }
        ctx.restore();
      }

      // ── Ball ──────────────────────────────────────────────────────────
      if (isActive) {
        // Colour: rainbow during approach, pitch-based otherwise
        const activeBallColor = inApproach ? glowColor : ballColor;

        const grad = ctx.createRadialGradient(
          BALL_SCREEN_X, ballY, 0, BALL_SCREEN_X, ballY, BALL_R * 2.4,
        );
        grad.addColorStop(0, activeBallColor);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(BALL_SCREEN_X, ballY, BALL_R * 2.4, 0, Math.PI * 2);
        ctx.fill();

        // During approach — extra large pulsing outer glow ring
        if (inApproach) {
          const outerPulse = 0.25 + 0.25 * Math.abs(Math.sin(timestamp / 220));
          const ringR = BALL_R * (3.5 + 0.8 * Math.abs(Math.sin(timestamp / 350)));
          ctx.globalAlpha = outerPulse;
          const ringGrad = ctx.createRadialGradient(BALL_SCREEN_X, ballY, BALL_R, BALL_SCREEN_X, ballY, ringR);
          ringGrad.addColorStop(0, glowColor);
          ringGrad.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = ringGrad;
          ctx.beginPath();
          ctx.arc(BALL_SCREEN_X, ballY, ringR, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }

        // Extra pulse glow during grace — signals "get ready"
        if (inGrace && !inApproach) {
          const pulse = 0.35 + 0.25 * Math.abs(Math.sin(timestamp / 250));
          ctx.globalAlpha = pulse;
          const gradBig = ctx.createRadialGradient(
            BALL_SCREEN_X, ballY, BALL_R, BALL_SCREEN_X, ballY, BALL_R * 4,
          );
          gradBig.addColorStop(0, '#ff9800');
          gradBig.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = gradBig;
          ctx.beginPath();
          ctx.arc(BALL_SCREEN_X, ballY, BALL_R * 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }

        ctx.fillStyle = activeBallColor;
        ctx.beginPath();
        ctx.arc(BALL_SCREEN_X, ballY, BALL_R, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.beginPath();
        ctx.arc(BALL_SCREEN_X - 5, ballY - 5, BALL_R * 0.38, 0, Math.PI * 2);
        ctx.fill();

        // Note name label just below ball
        ctx.font         = 'bold 13px Arial';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle    = inApproach ? glowColor : (inGrace ? '#ffb74d' : 'rgba(255,255,255,0.75)');
        ctx.fillText(currentNote.display || '', BALL_SCREEN_X, ballY + BALL_R + 5);
        ctx.textBaseline = 'alphabetic';
      } else {
        ctx.fillStyle = '#666';
        ctx.beginPath();
        ctx.arc(BALL_SCREEN_X, ballY, BALL_R, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── Note progress % ───────────────────────────────────────────────
      ctx.fillStyle    = 'rgba(255,255,255,0.85)';
      ctx.font         = 'bold 16px Arial';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(Math.round(noteProgress * 100) + '%', W / 2, CANVAS_H - 12);

      // ── Top info — "SING NOW" left · dots centre · "NEXT" right ─────────
      if (isActive) {
        const nextNote = ns[noteIdx + 1];
        const INFO_Y1  = 18;  // label row
        const INFO_Y2  = 42;  // note name row

        if (inApproach) {
          // ── APPROACH: "COMING UP" left + countdown right ──
          ctx.textAlign    = 'left';
          ctx.textBaseline = 'middle';
          ctx.font         = 'bold 10px Arial';
          ctx.fillStyle    = glowColor;
          ctx.fillText('COMING UP', PADDING + 10, INFO_Y1);

          ctx.font      = 'bold 22px Arial';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(currentNote.display || '–', PADDING + 10, INFO_Y2);

          // Duration hint
          if (currentNote.duration) {
            ctx.font      = '11px Arial';
            ctx.fillStyle = `hsla(${glowHue},100%,75%,0.7)`;
            ctx.fillText(currentNote.duration + 's', PADDING + 10, INFO_Y2 + 16);
          }

          // Countdown seconds (right side)
          const secsLeft = Math.ceil(APPROACH_SECS - approachElapsed);
          ctx.textAlign = 'right';
          ctx.font      = 'bold 28px Arial';
          ctx.fillStyle = glowColor;
          ctx.globalAlpha = 0.7 + 0.3 * Math.abs(Math.sin(timestamp / 300));
          ctx.fillText(secsLeft, W - PADDING - 10, INFO_Y2);
          ctx.globalAlpha = 1;

          // NEXT (smaller, below countdown)
          if (nextNote) {
            ctx.font      = '11px Arial';
            ctx.fillStyle = 'rgba(255,215,0,0.7)';
            ctx.fillText('next: ' + nextNote.display, W - PADDING - 10, INFO_Y2 + 16);
          }

          ctx.textBaseline = 'alphabetic';
        } else {
          // ── SING NOW (left) ──
          ctx.textAlign    = 'left';
          ctx.textBaseline = 'middle';
          ctx.font         = 'bold 10px Arial';
          ctx.fillStyle    = inGrace ? '#ffb74d' : barColor;
          ctx.fillText('SING NOW', PADDING + 10, INFO_Y1);

          ctx.font      = 'bold 22px Arial';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(currentNote.display || '–', PADDING + 10, INFO_Y2);

          // Duration line under note name
          const dur = currentNote.duration;
          if (dur) {
            ctx.font      = '11px Arial';
            ctx.fillStyle = inGrace ? 'rgba(255,183,77,0.7)' : 'rgba(255,255,255,0.5)';
            ctx.fillText(dur + 's', PADDING + 10, INFO_Y2 + 16);
          }

          // ── NEXT (right) — only if there is a next note ──
          if (nextNote) {
            ctx.textAlign    = 'right';
            ctx.font         = 'bold 10px Arial';
            ctx.fillStyle    = 'rgba(255,215,0,0.8)';
            ctx.fillText('NEXT', W - PADDING - 10, INFO_Y1);

            ctx.font      = 'bold 18px Arial';
            ctx.fillStyle = '#FFD700';
            ctx.fillText(nextNote.display || '–', W - PADDING - 10, INFO_Y2);
          }

          // ── Grace indicator: text for note 0 only, nothing for note 1+ (instant start) ──
          if (inGrace && noteIdx === 0) {
            const pulse = 0.65 + 0.35 * Math.abs(Math.sin(timestamp / 200));
            ctx.globalAlpha  = pulse;
            ctx.font         = 'bold 13px Arial';
            ctx.textAlign    = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle    = '#ffb74d';
            ctx.fillText('Get ready to sing..', W / 2, INFO_Y1 + 6);
            ctx.globalAlpha  = 1.0;
          } else if (!inGrace && currentFreq > 0 && !hasConfidentPitch) {
            // Detecting
            ctx.fillStyle = '#ff9800';
            ctx.font      = '11px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('detecting…', W / 2, INFO_Y1 + 6);
          }

          ctx.textBaseline = 'alphabetic';
        }
      }

      if ((noteProgress < 1 || inApproach) && isActive) {
        animationRef.current = requestAnimationFrame(draw);
      }
    };

    if (isActive) {
      animationRef.current = requestAnimationFrame(draw);
    } else {
      draw(performance.now());
    }

    return () => {
      running = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // currentNoteIndex in deps restarts the RAF for each new note (no remounting needed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, graceMs, currentNoteIndex]);

  return (
    <Box
      sx={{
        width: '100%',
        height,
        backgroundColor: '#0a0a0a',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </Box>
  );
};

export default PitchTracker;
